import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { usePlatform } from "@/components/platform/PlatformProvider";
import { useSupportEmail } from "@/hooks/useSupportEmail";

/**
 * Resolve the platform / brand variables that every Help Centre page
 * passes to `interpolate()` so `{{brand}}`, `{{entity}}` and
 * `{{supportEmail}}` placeholders never leak into the rendered DOM.
 *
 * Task #1514 — these tokens were appearing verbatim under /help/* in
 * topic descriptions, article cards, breadcrumbs and JSON-LD because
 * `t(topic.descriptionKey, { defaultValue: "" })` was called without
 * variables and the help-article body interpolation didn't extend to
 * titles or summaries.
 *
 * `entity` is sourced from the `legal` namespace (same value across
 * all 9 supported locales today, but the lookup keeps us honest if
 * that ever changes).
 */
export function useHelpVars(): Record<string, string> {
  const platform = usePlatform();
  const supportEmail = useSupportEmail();
  const { t } = useTranslation("legal");
  return useMemo(
    () => ({
      brand: platform.name,
      entity: t("common.entity", { defaultValue: platform.name }),
      supportEmail,
    }),
    [platform.name, supportEmail, t],
  );
}
